/**
 * NotificationDrawer — right-side slide-out drawer.  Replaces the old
 * cramped dropdown.
 *
 * Layout:
 *   - Header with title, connection status dot, mark-all-read, close
 *   - Tabs: Today / All / Settings (+ Broadcast for admins/managers)
 *   - Filter chips per category (Today + All tabs)
 *   - Date-grouped lists (Today / Yesterday / This week / Older)
 *   - Group-key threading: same group_key in the same day collapses to
 *     "10 wins in Bingo today" with expand button.
 *   - Swipe-to-dismiss on individual rows (handled in NotificationRow).
 *   - Empty states with personality.
 */
import { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, CheckCircle, BellRinging, FunnelSimple, CaretDown } from '@phosphor-icons/react';
import { useNotifications } from './NotificationProvider';
import NotificationRow from './NotificationRow';
import BroadcastComposer from './BroadcastComposer';
import NotificationSettingsPanel from './NotificationSettingsPanel';
import { useAuth } from '../context/AuthContext';

const TABS = (canBroadcast) => [
  { k: 'today',     label: 'Today',     show: true },
  { k: 'all',       label: 'All',       show: true },
  { k: 'broadcast', label: 'Broadcast', show: canBroadcast },
  { k: 'settings',  label: 'Settings',  show: true },
];

function bucketLabel(iso) {
  if (!iso) return 'Older';
  const d = new Date(iso);
  const now = new Date();
  const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dayDiff = Math.floor((startOfDay(now) - startOfDay(d)) / 86400000);
  if (dayDiff <= 0) return 'Today';
  if (dayDiff === 1) return 'Yesterday';
  if (dayDiff < 7) return 'This week';
  if (dayDiff < 30) return 'This month';
  return 'Older';
}

const NotificationDrawer = ({ open, onClose }) => {
  const { user } = useAuth();
  const canBroadcast = user?.role === 'admin' || user?.is_manager;
  const navigate = useNavigate();
  const {
    items, unread, loading, categories,
    markRead, markAllRead, remove,
    hasMore, loadMore,
    connected: connectedSse,
  } = useNotifications();

  const [tab, setTab] = useState('today');
  const [filter, setFilter] = useState(null);   // null = all categories
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    if (open) setTab((t) => (t === 'broadcast' && !canBroadcast ? 'today' : t));
  }, [open, canBroadcast]);

  // Lock body scroll while open + ensure cleanup if the drawer unmounts.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.setAttribute('data-modal-locked', '');
    return () => {
      document.documentElement.style.overflow = prevOverflow;
      document.body.removeAttribute('data-modal-locked');
    };
  }, [open]);

  // Filter by tab + category
  const visible = useMemo(() => {
    let list = items;
    if (tab === 'today') {
      list = list.filter(x => bucketLabel(x.created_at) === 'Today');
    }
    if (filter) {
      list = list.filter(x => x.category === filter);
    }
    return list;
  }, [items, tab, filter]);

  // Group by date bucket + group_key collapsing
  const groupedSections = useMemo(() => {
    const buckets = {};
    for (const item of visible) {
      const b = bucketLabel(item.created_at);
      buckets[b] = buckets[b] || [];
      buckets[b].push(item);
    }
    // For each bucket, collapse repeated group_key into a single row (with count)
    const ordered = ['Today', 'Yesterday', 'This week', 'This month', 'Older'];
    return ordered.filter(b => buckets[b]).map(b => {
      const inBucket = buckets[b];
      const collapsed = [];
      const byGroup = {};
      for (const it of inBucket) {
        if (!it.group_key) { collapsed.push(it); continue; }
        if (!byGroup[it.group_key]) {
          byGroup[it.group_key] = { lead: it, members: [it] };
          collapsed.push({ __group__: true, key: it.group_key, lead: it, members: byGroup[it.group_key].members });
        } else {
          byGroup[it.group_key].members.push(it);
        }
      }
      return { label: b, items: collapsed };
    });
  }, [visible]);

  const handleOpen = (item) => {
    if (item.url && item.url !== '/') {
      // Strip query/hash for navigate, then let route component re-render
      try {
        const u = new URL(item.url, window.location.origin);
        navigate(u.pathname + u.search + u.hash);
      } catch (_) {
        navigate(item.url);
      }
    }
    onClose?.();
  };

  // Keep a "rendered" flag separately from `open` so we can unmount after
  // the exit transition finishes — but never get stuck.  If `open` is ever
  // false while we're still rendered, we wait 320ms (longer than the longest
  // transition) and force-unmount.  No animation library, no race conditions.
  // Always start FALSE — never `useState(open)` — so the drawer never
  // pre-mounts at app boot regardless of upstream state weirdness.
  const [rendered, setRendered] = useState(false);
  const unmountTimer = useRef(null);
  useEffect(() => {
    if (open) {
      if (unmountTimer.current) {
        clearTimeout(unmountTimer.current);
        unmountTimer.current = null;
      }
      setRendered(true);
      return;
    }
    if (!rendered) return;
    // Schedule unmount after CSS transition completes
    unmountTimer.current = setTimeout(() => {
      setRendered(false);
      unmountTimer.current = null;
    }, 320);
    return () => {
      if (unmountTimer.current) {
        clearTimeout(unmountTimer.current);
        unmountTimer.current = null;
      }
    };
  }, [open, rendered]);

  // ESC key closes — safety net
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!rendered) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <>
      {/* Backdrop — always in DOM while rendered, opacity drives the visual.
       * `pointer-events: none` when closed makes any rare stuck state
       * impossible — even if it visually persists for a frame, it can't
       * trap clicks. */}
      <div
        className="fixed inset-0 z-[70] cs-notif-backdrop"
        data-state={open ? 'open' : 'closed'}
        onClick={onClose}
        data-testid="notification-drawer-backdrop"
      />
      {/* Drawer */}
      <aside
        data-state={open ? 'open' : 'closed'}
        data-testid="notification-drawer"
        className="fixed top-0 right-0 bottom-0 z-[71] flex flex-col overflow-hidden cs-notif-drawer cs-notif-drawer-slide"
        style={{
          width: 'min(420px, 100vw)',
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
            {/* Header */}
            <div className="px-4 py-3 flex items-center gap-3 border-b shrink-0"
              style={{ borderColor: 'var(--cs-border)' }}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #CD040B 0%, #7F0207 100%)',
                  boxShadow: '0 6px 20px var(--cs-notif-glow)',
                }}>
                <BellRinging size={18} weight="fill" color="#fff" style={{ position: 'relative', zIndex: 1 }} />
                <span aria-hidden className="absolute inset-x-0 top-0 h-1/2"
                  style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.18), transparent)' }} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <div className="text-[15px] font-bold leading-tight" style={{ color: 'var(--cs-text)', fontFamily: 'Outfit, sans-serif' }}>
                    Notifications
                  </div>
                  {/* Live connection indicator */}
                  <span aria-label={connectedSse ? 'Live' : 'Reconnecting'}
                    className="w-1.5 h-1.5 rounded-full"
                    style={{
                      background: connectedSse ? '#10B981' : '#71717A',
                      boxShadow: connectedSse ? '0 0 6px rgba(16,185,129,0.6)' : 'none',
                    }} />
                </div>
                <div className="text-[11px]" style={{ color: 'var(--cs-text-muted)' }}>
                  {unread > 0 ? `${unread} unread` : 'All caught up'}
                </div>
              </div>
              {unread > 0 && (
                <button
                  type="button"
                  onClick={markAllRead}
                  className="text-[11px] font-bold px-2.5 py-1.5 rounded-lg transition-colors"
                  style={{ color: 'var(--cs-notif-accent)', background: 'var(--cs-notif-surface)' }}
                  data-testid="notif-mark-all-read"
                >
                  <CheckCircle size={12} weight="bold" className="inline mr-1" />Read all
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                data-testid="notification-drawer-close"
                className="p-2 rounded-lg hover:bg-[var(--cs-bg-hover)]"
                style={{ color: 'var(--cs-text-muted)' }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Tabs */}
            <div className="px-3 py-2 flex items-center gap-1 border-b shrink-0"
              style={{ borderColor: 'var(--cs-border)' }}>
              {TABS(canBroadcast).filter(t => t.show).map(t => (
                <button
                  key={t.k}
                  type="button"
                  onClick={() => setTab(t.k)}
                  data-testid={`notif-tab-${t.k}`}
                  className="flex-1 px-3 py-2.5 rounded-lg text-[12px] font-semibold transition-all"
                  style={tab === t.k
                    ? { background: 'var(--cs-notif-wash)', color: 'var(--cs-notif-accent)', border: '1px solid var(--cs-notif-border)' }
                    : { color: 'var(--cs-text-muted)', border: '1px solid transparent' }}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-3">
              {(tab === 'today' || tab === 'all') && (
                <>
                  {/* Filter chips */}
                  {Object.keys(categories).length > 0 && (
                    <div className="flex items-center gap-1.5 overflow-x-auto pb-2 mb-2"
                      style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
                      <button
                        type="button"
                        onClick={() => setFilter(null)}
                        className="shrink-0 px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all"
                        style={filter === null
                          ? { background: 'var(--cs-notif-wash)', color: 'var(--cs-notif-accent)', border: '1px solid var(--cs-notif-border)' }
                          : { background: 'var(--cs-bg-surface)', color: 'var(--cs-text-muted)', border: '1px solid var(--cs-border)' }}
                        data-testid="notif-filter-all"
                      >
                        <FunnelSimple size={11} weight="bold" className="inline mr-1" />All
                      </button>
                      {Object.entries(categories).map(([k, m]) => {
                        const has = items.some(x => x.category === k);
                        if (!has) return null;
                        return (
                          <button
                            key={k}
                            type="button"
                            onClick={() => setFilter(filter === k ? null : k)}
                            className="shrink-0 px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all"
                            style={filter === k
                              ? { background: `${m.accent}22`, color: m.accent, border: `1px solid ${m.accent}55` }
                              : { background: 'var(--cs-bg-surface)', color: 'var(--cs-text-muted)', border: '1px solid var(--cs-border)' }}
                            data-testid={`notif-filter-${k}`}
                          >
                            {m.label}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {visible.length === 0 ? (
                    <div className="flex flex-col items-center justify-center text-center py-16 px-6 cs-empty">
                      <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-3"
                        style={{ background: 'var(--cs-notif-surface)', border: '1px solid var(--cs-notif-border)' }}>
                        <BellRinging size={26} weight="duotone" style={{ color: 'var(--cs-notif-accent)' }} />
                      </div>
                      <div className="text-[14px] font-semibold mb-1" style={{ color: 'var(--cs-text)' }}>
                        {tab === 'today' ? 'Nothing today' : 'No notifications yet'}
                      </div>
                      <div className="text-[11.5px] max-w-[280px]" style={{ color: 'var(--cs-text-muted)' }}>
                        New activity from your team, goals, and wins will land here in real time.
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {groupedSections.map(section => (
                        <div key={section.label} data-testid={`notif-bucket-${section.label}`}>
                          <div className="text-[10px] font-black uppercase tracking-[1.5px] mb-1.5 pl-1"
                            style={{ color: 'var(--cs-text-dim)' }}>
                            {section.label}
                          </div>
                          <ul className="space-y-1.5">
                            {section.items.map(it => {
                              if (it.__group__) {
                                const expanded = expandedGroups[it.key];
                                const rest = it.members.slice(1);
                                return (
                                  <li key={it.key}>
                                    <NotificationRow
                                      item={it.lead}
                                      categoryMeta={categories[it.lead.category]}
                                      onOpen={handleOpen}
                                      onRead={markRead}
                                      onDismiss={remove}
                                    />
                                    {rest.length > 0 && (
                                      <button
                                        type="button"
                                        onClick={() => setExpandedGroups(g => ({ ...g, [it.key]: !g[it.key] }))}
                                        className="mt-1 ml-3 text-[10px] font-semibold inline-flex items-center gap-1"
                                        style={{ color: 'var(--cs-text-muted)' }}
                                        data-testid={`notif-group-expand-${it.key}`}
                                      >
                                        <CaretDown size={10} weight="bold"
                                          style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 160ms' }} />
                                        {expanded ? 'Hide' : `+ ${rest.length} more`}
                                      </button>
                                    )}
                                    {expanded && (
                                      <ul className="space-y-1.5 mt-1.5 ml-3 pl-2 border-l-2"
                                        style={{ borderColor: 'var(--cs-border)' }}>
                                        {rest.map(m => (
                                          <NotificationRow key={m.id} item={m}
                                            categoryMeta={categories[m.category]}
                                            onOpen={handleOpen}
                                            onRead={markRead}
                                            onDismiss={remove} />
                                        ))}
                                      </ul>
                                    )}
                                  </li>
                                );
                              }
                              return (
                                <NotificationRow
                                  key={it.id}
                                  item={it}
                                  categoryMeta={categories[it.category]}
                                  onOpen={handleOpen}
                                  onRead={markRead}
                                  onDismiss={remove}
                                />
                              );
                            })}
                          </ul>
                        </div>
                      ))}
                      {hasMore && tab === 'all' && (
                        <button
                          type="button"
                          onClick={loadMore}
                          disabled={loading}
                          data-testid="notif-load-more"
                          className="w-full mt-2 py-2 rounded-lg text-[11px] font-semibold"
                          style={{ background: 'var(--cs-bg-hover)', color: 'var(--cs-text-muted)' }}>
                          {loading ? 'Loading…' : 'Load older'}
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}

              {tab === 'broadcast' && canBroadcast && (
                <BroadcastComposer categories={categories} />
              )}

              {tab === 'settings' && (
                <NotificationSettingsPanel />
              )}
            </div>
          </aside>
    </>,
    document.body,
  );
};

export default NotificationDrawer;
